import { useTreeEntries } from "forest/file-tree/use-tree";
import { AlertTriangle, Play, Settings2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useBearSandbox } from "@/components/BearSandboxProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import BearInspectorOverlay from "@/panes/bear-inspector-overlay";
import BearPartMenu from "@/panes/bear-part-menu";
import { ModelMarkup, type ModelMarkupHost } from "@/panes/model-markup";

type BearModelPaneProps = {
  onOpenPath: (path: string) => void;
  entryPoint?: string;
  autoRun?: boolean;
  /** Where this view keeps markup: given one, the view offers the whole of
   *  model markup (`model-markup.tsx`); without one, none of it. */
  markup?: ModelMarkupHost;
};

const BearModelPane = ({
  onOpenPath,
  entryPoint,
  autoRun = false,
  markup,
}: BearModelPaneProps) => {
  const { attachContainer, sourceState, run, playground, getModelSelection } =
    useBearSandbox();
  const entries = useTreeEntries(playground);
  const paths = useMemo(() => new Set(entries?.map((e) => e.path)), [entries]);
  const fileExists = useCallback((path: string) => paths.has(path), [paths]);
  const [exportName, setExportName] = useState(
    () => getModelSelection(entryPoint).exportName ?? "",
  );
  const [propsText, setPropsText] = useState(() =>
    JSON.stringify(getModelSelection(entryPoint).props ?? {}),
  );
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const started = useRef(false);
  const { error, isBuilding, hasBuilt } = sourceState;
  const errorDetails = error ? (error.stack ?? error.message) : null;
  const handleContainerRef = useCallback(
    (element: HTMLDivElement | null) => attachContainer(element),
    [attachContainer],
  );

  useEffect(() => {
    if (autoRun && !started.current) {
      started.current = true;
      void run({ entryPoint });
    }
  }, [autoRun, entryPoint, run]);

  const runSelected = useCallback(() => {
    try {
      const parsed: unknown = propsText.trim() ? JSON.parse(propsText) : {};
      if (
        typeof parsed !== "object" ||
        parsed === null ||
        Array.isArray(parsed)
      ) {
        setSelectionError("Props must be a JSON object");
        return;
      }
      setSelectionError(null);
      void run({
        entryPoint,
        exportName: exportName.trim(),
        props: parsed as Record<string, unknown>,
      });
    } catch {
      setSelectionError("Props must be valid JSON");
    }
  }, [entryPoint, exportName, propsText, run]);

  return (
    <div
      ref={handleContainerRef}
      className="relative h-full w-full"
      data-sandbox-container
    >
      {markup && <ModelMarkup host={markup} />}
      <div className="absolute top-2 right-2 z-20 flex items-center rounded-md border bg-background/95 shadow-sm">
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Model options"
              title="Model options"
            >
              <Settings2 className="size-3.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-80 space-y-3 p-3">
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="model-export">
                Export
              </label>
              <Input
                id="model-export"
                className="h-8 text-xs"
                placeholder="default or sole component"
                value={exportName}
                onChange={(event) => setExportName(event.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium" htmlFor="model-props">
                Props (JSON)
              </label>
              <Textarea
                id="model-props"
                className="min-h-16 font-mono text-xs"
                placeholder="{}"
                value={propsText}
                onChange={(event) => setPropsText(event.target.value)}
              />
            </div>
            {selectionError ? (
              <p className="text-xs text-destructive">{selectionError}</p>
            ) : null}
            <Button size="sm" className="h-7 text-xs" onClick={runSelected}>
              Render
            </Button>
          </PopoverContent>
        </Popover>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="Run model"
          title="Run model"
          onClick={runSelected}
          disabled={isBuilding}
        >
          <Play className="size-3.5" />
        </Button>
      </div>
      {error ? (
        <div className="absolute inset-0 flex items-center justify-center px-6 py-8">
          <div className="max-w-lg rounded-lg border border-destructive/30 bg-background/95 p-6 shadow-lg">
            <div className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" aria-hidden="true" />
              <h2 className="font-semibold">Unable to render model</h2>
            </div>
            {errorDetails ? (
              <pre className="mt-4 overflow-x-auto rounded-md border border-destructive/20 bg-muted/40 p-3 text-xs text-muted-foreground">
                <code className="font-mono">{errorDetails}</code>
              </pre>
            ) : null}
          </div>
        </div>
      ) : isBuilding ? (
        <div className="animate-in fade-in absolute inset-0 flex items-center justify-center bg-black/20 duration-150">
          <span className="text-sm text-white/60">Building model...</span>
        </div>
      ) : hasBuilt ? (
        <>
          <BearInspectorOverlay
            fileExists={fileExists}
            onOpenPath={onOpenPath}
          />
          <BearPartMenu fileExists={fileExists} openPath={onOpenPath} />
        </>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="text-sm text-muted-foreground">No model rendered yet</p>
        </div>
      )}
    </div>
  );
};

export default BearModelPane;
